import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Circle } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Map as MapIcon } from "lucide-react";

// Fix Leaflet default icon paths (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const CENTERS = {
  "Grand Montréal": [45.6, -73.55],
  "Montréal": [45.5017, -73.5673],
  "Laval": [45.6066, -73.7124],
  "Repentigny": [45.7422, -73.4501],
  "Joliette": [46.0224, -73.4416],
  "Lavaltrie": [45.8827, -73.2837],
};

const RADII = [
  { v: 0, l: "Aucun" },
  { v: 5, l: "5 km" },
  { v: 10, l: "10 km" },
  { v: 25, l: "25 km" },
  { v: 50, l: "50 km" },
  { v: 100, l: "100 km" },
];

// Haversine distance in km
function distanceKm(a, b) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

const scoreColor = (s) => (s >= 85 ? "#10B981" : s >= 70 ? "#06B6D4" : s >= 50 ? "#F59E0B" : "#94A3B8");

export default function MapView() {
  const [items, setItems] = useState([]);
  const [center, setCenter] = useState("Grand Montréal");
  const [radius, setRadius] = useState(50);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/map/opportunities");
        setItems(data.items || []);
      } catch {}
    })();
  }, []);

  const centerCoord = CENTERS[center] || CENTERS["Grand Montréal"];
  const filtered = useMemo(() => {
    if (!radius) return items;
    return items.filter((o) => o.lat && o.lng && distanceKm(centerCoord, [o.lat, o.lng]) <= radius);
  }, [items, radius, centerCoord]);

  return (
    <div className="space-y-4" data-testid="map-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
            <MapIcon className="h-7 w-7 text-cyan-400" /> Carte des opportunités
          </h1>
          <p className="text-muted-foreground mt-1">Vue géographique des prospects et appels d'offres dans le Grand Montréal.</p>
        </div>
        <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30" data-testid="map-count-badge">
          {filtered.length} opportunité{filtered.length > 1 ? "s" : ""}
        </Badge>
      </div>

      <Card className="p-4 bg-card border-border flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Centre</span>
          <Select value={center} onValueChange={setCenter}>
            <SelectTrigger className="w-[200px]" data-testid="map-center-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(CENTERS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Rayon</span>
          <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
            <SelectTrigger className="w-[140px]" data-testid="map-radius-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RADII.map((r) => <SelectItem key={r.v} value={String(r.v)}>{r.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden border-border" data-testid="map-container-card">
        <div style={{ height: 600, width: "100%" }}>
          <MapContainer center={centerCoord} zoom={9} scrollWheelZoom style={{ height: "100%", width: "100%" }} key={center}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
            {radius > 0 && (
              <Circle center={centerCoord} radius={radius * 1000} pathOptions={{ color: "#06B6D4", fillColor: "#06B6D4", fillOpacity: 0.05, weight: 1 }} />
            )}
            {filtered.map((o) => (
              <CircleMarker
                key={o.opportunity_id}
                center={[o.lat, o.lng]}
                radius={8}
                pathOptions={{ color: scoreColor(o.ai_score || 0), fillColor: scoreColor(o.ai_score || 0), fillOpacity: 0.7, weight: 2 }}
              >
                <Popup>
                  <div style={{ minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{o.company_name}</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>
                      {o.city} · {o.industry}
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                      Score IA: <strong style={{ color: scoreColor(o.ai_score || 0) }}>{o.ai_score || "—"}/100</strong>
                    </div>
                    <Link to={`/opportunites/${o.opportunity_id}`} style={{ color: "#0891B2", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                      Voir le détail →
                    </Link>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </Card>

      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-emerald-400" /> Score ≥ 85</div>
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-cyan-400" /> Score 70-84</div>
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-amber-400" /> Score 50-69</div>
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-slate-400" /> Score &lt; 50</div>
      </div>
    </div>
  );
}
